import { trackEvent } from '@/utils/analytics'
import {
  useEffect,
  useRef,
  useState,
  useMemo,
  type KeyboardEvent,
  useCallback,
} from 'react'
import { flushSync } from 'react-dom'
import { useNavigate, useParams } from 'react-router-dom'
import clsx from 'clsx'
import { Send, Square } from 'lucide-react'
import { v4 as uuidv4 } from 'uuid'
import { apiService, type Message, type MessageVersionGroupResponse } from '../../service/api'
import {
  WorkflowProgressPart as WorkflowProgressPartSchema,
  PendingSharePart as PendingSharePartSchema,
} from '@intuigence/response-parts'
import { Button } from '@/components/ui/button'
import { Textarea } from '../ui/textarea'
import { useAppStore } from '@/store'
import { isAnnotatorRole } from '@/types/users'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import ChatHeader from './ChatHeader'
import ChatContextChips from './ChatContextChips'
import ContextHeader from './ContextHeader'
import Messages from './Messages'
import ChatVoice from './ChatVoice'
import EmptyChatState from './EmptyChatState'
import { ROLES } from '@/store/chatSlice'
import { normalizeAgentName, AgentId, AGENT_ALIASES, findUnavailableMention, resolveResponseDisplayAgent } from '@/constants/agentNames'
import LLMCallDropdown from '../LLMCallDropdown'
import { useFeatureEnabled } from '@/features/featureAccess/useFeatureAccess'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'
import {
  AgentMentionAutocomplete,
  AGENTS,
  AGENT_REGISTRY,
  type AgentOption,
} from './AgentMentionAutocomplete'
import { getCurrentDirection } from '@/i18n/config'
import {
  readSSEMessages,
  SSEInactivityTimeoutError,
} from '@/utils/sse'
import { VERCEL_AI_STREAM_HEADER, VERCEL_AI_STREAM_VERSION } from '@/features/chatV2/constants'
import ZoomMeeting from '../ZoomMeeting'
import type { VersionInfo } from '@/features/chatV2/types'
import type { ToolProgressData } from '@/features/chatV2/components/messages/ToolProgressStatus'
import { parseToolProgressFrame } from '@/features/chatV2/utils/parseToolProgressFrame'
import { hasReasoningPathChanged } from './reasoningPathUtils'

/** Local version entry for V1 Message type */
type V1VersionEntry = {
  responses: Message[]
  userMessages: Message[]
  currentIndex: number
  liveMessage: Message | null
  liveUserMessage: Message | null
}

type ChatInterfaceProps = {
  context_id?: string
  context_type: 'workspace' | 'document'
  onClose?: () => void
  chatMode: 'canvas' | 'global'
  autoSendMessage?: boolean
  navigationKey?: string
  initialMessage?: string  // Message from navigation state
}

export const CHAT_SEND_BUTTON_ID = 'chat-send-button'

/**
 * W16 / TIME-198: how long to hold the `failed` tool-progress state
 * visible before clearing. Without the hold, the renderer's red-dot
 * styling never paints because the parent unmounts the line within
 * the same React batch as the failed frame. Mirrors `FAILED_HOLD_MS`
 * in chatV2's useChatV2 hook to keep V1 and V2 timing consistent.
 */
const FAILED_HOLD_MS = 800

type AssistantStreamTerminalStatus = 'completed' | 'error' | 'cancelled'

type AssistantStreamTerminalEvent = {
  id: string
  conversation_id: string
  timestamp: string
  status: AssistantStreamTerminalStatus
  user_message_id?: string | null
  error_text?: string
}

const ASSISTANT_MESSAGE_END_EVENT = 'assistant_message_end'
const ASSISTANT_MESSAGE_ERROR_EVENT = 'assistant_message_error'
const ASSISTANT_MESSAGE_CANCELLED_EVENT = 'assistant_message_cancelled'
// This is only a last-resort transport watchdog for hung SSE connections.
const STREAM_INACTIVITY_TIMEOUT_MS = 120_000
const STREAM_RECOVERY_RETRY_DELAYS_MS = [0, 1000, 2000, 4000, 8000, 16000]

type AssistantHistorySyncStatus =
  | 'missing'
  | 'pending'
  | 'completed'
  | 'error'
  | 'stopped'
  | 'interrupted'

type AssistantHistorySyncResult = {
  status: AssistantHistorySyncStatus
  assistantId: string | null
}

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms)
  })

const getAssistantHistorySyncStatus = (
  assistantMessage: Message | null | undefined
): AssistantHistorySyncStatus => {
  if (!assistantMessage) return 'missing'

  const reasoningStatus = assistantMessage.reasoning_path?.status
  if (
    reasoningStatus === 'completed' ||
    reasoningStatus === 'error' ||
    reasoningStatus === 'stopped'
  ) {
    return reasoningStatus
  }

  if (reasoningStatus === 'in_progress') {
    return 'pending'
  }

  if (assistantMessage.content.trim() || assistantMessage.chart_data?.length) {
    return 'completed'
  }

  return 'pending'
}

const finalizeReasoningPathState = (
  reasoningPath: Message['reasoning_path'] | undefined,
  finalStatus: 'completed' | 'error' | 'stopped'
) => {
  if (!reasoningPath) return reasoningPath
  return {
    ...reasoningPath,
    status: finalStatus,
    steps: reasoningPath.steps.map((step) => ({
      ...step,
      completed: true,
    })),
  }
}

interface AzureConfig {
  subscriptionKey: string
  region: string
}

export const AZURE_CONFIG: AzureConfig = {
  subscriptionKey: import.meta.env.VITE_AZURE_CONFIG_SPEECH_KEY,
  region: import.meta.env.VITE_AZURE_CONFIG_SPEECH_REGION,
}

const ChatInterface = ({
  context_type,
  onClose,
  chatMode = 'canvas',
  autoSendMessage = false,
  navigationKey,
  initialMessage,  // Extract from props
}: ChatInterfaceProps) => {
  const { t } = useTranslation()
  const callsEnabled = useFeatureEnabled('agent_calls')
  const isRTL = getCurrentDirection() === 'rtl'
  const {
    audioOutEnabled,
    speechQueue,
    isAISpeaking,
    currentPlayer,
    chatMessages,
    chatConversations,
    setChatConversations,
    context_id,
    currentConversationId,
    activeAgent,
    isChatLoading,
    messagesLoading,
    boardList,
    // Actions
    setAudioOutEnabled,
    addToSpeechQueue,
    processQueue,
    flushSpeech,
    setAISpeaking,
    setChatMessages,
    setCurrentConversationId,
    setActiveAgent,
    setIsChatLoading,
    loadChatConversations,
    sendUserMessage,
    registerStopHandler,
    unregisterStopHandler,
    setGlobalStopResponse,
    stopReply,
    setContextId,
    loadBoardList,
    selectedAgent,
    agents: storeAgents,
    // Chat-scope chips (board card kebab → "Add to Chat")
    chatContextDocIds,
    removeDocFromChatContext,
    documents: boardDocuments,
    selectedDocuments: boardSelectedDocuments,
    user,
  } = useAppStore()

  // Annotators have no AI access — the composer is disabled (the backend also
  // 403s the chat request). They can still read existing conversations.
  const isAnnotator = isAnnotatorRole(user?.role)

  // Resolve each attached doc id to a human-readable name. Defensive
  // against test mocks that don't seed chatContextDocIds — fall back
  // to an empty array if it's missing or non-iterable.
  const chatContextChips = useMemo(() => {
    if (!Array.isArray(chatContextDocIds) || chatContextDocIds.length === 0) return []
    return chatContextDocIds.map((id: string) => {
      const fromDocs = boardDocuments?.find((d: { id?: string }) => d.id === id)
      const fromSel = boardSelectedDocuments?.find(
        (d: { document_id?: string }) => d.document_id === id
      )
      const name =
        (fromDocs as { name?: string } | undefined)?.name ??
        (fromSel as { name?: string } | undefined)?.name ??
        id
      return { id, name }
    })
  }, [chatContextDocIds, boardDocuments, boardSelectedDocuments])

  const streamAbortControllerRef = useRef<AbortController | null>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [localInputValue, setLocalInputValue] = useState('')
  const [pendingAutoSendMessage, setPendingAutoSendMessage] = useState<string | null>(null)
  const [isRecording, setIsRecording] = useState(false)
  const currentMessageIdRef = useRef<string | null>(null)
  const [documentSelectVal, setDocumentSelected] = useState('')
  const [editingMessage, setEditingMessage] = useState<Message | null>(null)
  const [responseVersions, setResponseVersions] = useState<Map<string, V1VersionEntry>>(new Map())
  // W16 / TIME-198: ephemeral tool-progress status line. Driven from
  // the SSE loop by detecting frames with `type==='tool_progress'`.
  // Cleared on terminal status (`completed`/`failed`) and at every
  // stream-termination site so a stale line never outlives the turn.
  const [toolProgress, setToolProgress] = useState<ToolProgressData | null>(null)
  // W16 / TIME-198: timer that briefly holds the `failed` state
  // visible before clearing. Without the hold, the parent unmounts
  // the line within a tick of the failed frame and the renderer's
  // red-dot styling never paints — operators see the line "silently
  // disappear" instead of seeing what failed.
  const failedHoldTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clearToolProgress = useCallback(() => {
    if (failedHoldTimerRef.current) {
      clearTimeout(failedHoldTimerRef.current)
      failedHoldTimerRef.current = null
    }
    setToolProgress(null)
  }, [])
  const lastCancelTimestampRef = useRef<number>(0)
  // RAF batching for stream frames — keeps the main thread free for
  // scroll/input events during high-frequency SSE streaming.
  const streamFrameBufferRef = useRef<Message[]>([])
  const streamRafIdRef = useRef<number | null>(null)
  const chatMessagesRef = useRef(chatMessages)
  const currentConversationIdRef = useRef(currentConversationId)
  const isChatLoadingRef = useRef(isChatLoading)
  const responseVersionsRef = useRef(responseVersions)
  // Live ref so the send guard reads the CURRENT role even if the role hydrates
  // after handleSendMessage was memoized (mirrors the refs above — avoids a stale
  // closure without widening the callback's deps).
  const isAnnotatorRef = useRef(isAnnotator)
  const navigate = useNavigate()
  const { chat_id } = useParams() || {}
  const inputValue = localInputValue

  chatMessagesRef.current = chatMessages
  currentConversationIdRef.current = currentConversationId
  isChatLoadingRef.current = isChatLoading
  responseVersionsRef.current = responseVersions
  isAnnotatorRef.current = isAnnotator

  const abortStreamRequest = useCallback(() => {
    if (!streamAbortControllerRef.current) return
    streamAbortControllerRef.current.abort()
    streamAbortControllerRef.current = null
  }, [])

  /** Fetch archived versions from API and restore the responseVersions map. */
  const loadVersionsForConversation = useCallback(async (conversationId: string, isCancelled: () => boolean) => {
    try {
      const groups: MessageVersionGroupResponse[] = await apiService.getMessageVersions(conversationId)
      if (isCancelled()) return
      if (!groups || groups.length === 0) {
        setResponseVersions(new Map())
        return
      }

      const versionsMap = new Map<string, V1VersionEntry>()
      for (const group of groups) {
        const userMsgId = group.version_group_id
        const responses: Message[] = []
        const userMessages: Message[] = []

        for (const version of group.versions) {
          // Only push paired entries so the two arrays stay the same length.
          // Skip versions that are missing either the assistant or user message.
          const assistantMsg = version.assistant_messages[0]
          if (!assistantMsg || !version.user_message) continue

          responses.push({
            id: assistantMsg.id,
            conversation_id: conversationId,
            role: assistantMsg.role as Message['role'],
            content: assistantMsg.content,
            timestamp: assistantMsg.timestamp,
            agent_used: assistantMsg.agent_used ?? undefined,
            chart_data: assistantMsg.chart_data ?? undefined,
            reasoning_path: assistantMsg.reasoning_path ?? undefined,
            // ENG-2416 §10: carry persisted citations through on reload so
            // the Sources list renders for historical messages too.
            sources: (assistantMsg.sources as Message['sources']) ?? undefined,
          })

          userMessages.push({
            id: version.user_message.id,
            conversation_id: conversationId,
            role: version.user_message.role as Message['role'],
            content: version.user_message.content,
            timestamp: version.user_message.timestamp,
          })
        }

        if (responses.length > 0) {
          versionsMap.set(userMsgId, {
            responses,
            userMessages,
            currentIndex: responses.length, // point to "live" version
            liveMessage: null,
            liveUserMessage: null,
          })
        }
      }

      setResponseVersions((prev) => {
        if (versionsMap.size === 0) return prev.size === 0 ? prev : new Map()
        // Merge API versions with any locally-created entries to avoid
        // overwriting version history added by a regeneration that raced
        // ahead of this API call.
        const merged = new Map(versionsMap)
        for (const [key, entry] of prev) {
          if (!merged.has(key)) {
            merged.set(key, entry)
          }
        }
        return merged
      })
    } catch (error) {
      if (!isCancelled()) {
        console.error('Failed to load message versions:', error)
      }
    }
  }, [])

  // Load archived versions when conversation changes (covers both explicit selection and auto-load)
  useEffect(() => {
    if (!currentConversationId) return
    let cancelled = false
    loadVersionsForConversation(currentConversationId, () => cancelled)
    return () => { cancelled = true }
  }, [currentConversationId, loadVersionsForConversation])

  // Cleanup streaming resources on unmount to prevent stale timers/event sources.
  useEffect(() => {
    return () => {
      abortStreamRequest()
      if (currentMessageIdRef.current) {
        unregisterStopHandler(currentMessageIdRef.current)
      }
      if (streamRafIdRef.current !== null) {
        cancelAnimationFrame(streamRafIdRef.current)
        streamRafIdRef.current = null
      }
      streamFrameBufferRef.current = []
      setIsChatLoading(false)
    }
  }, [abortStreamRequest, unregisterStopHandler, setIsChatLoading])

  // Agent mention autocomplete state
  const [showMentionAutocomplete, setShowMentionAutocomplete] = useState(false)
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0)
  const [mentionFilterQuery, setMentionFilterQuery] = useState('')
  const [cursorPosition, setCursorPosition] = useState(0)

  // Animated placeholder state
  const PLACEHOLDER_TEXTS = [
    t('chat:interface.placeholder'),
    t('chat:mention.hint'),
    t('chat:mention.placeholders.pam'),
    t('chat:mention.placeholders.aice'),
    t('chat:mention.placeholders.max'),
    t('chat:mention.placeholders.ella'),
  ]
  const [placeholderIndex, setPlaceholderIndex] = useState(0)
  const animatedPlaceholder = PLACEHOLDER_TEXTS[placeholderIndex]

  const isCanvasChat = chatMode === 'canvas'

  // Capture auto-send message from props (survives resetChatState)
  useEffect(() => {
    if (autoSendMessage && initialMessage && !autoSendTriggeredRef.current) {
      setPendingAutoSendMessage(initialMessage)  // Read from props, not global store
    }
  }, [initialMessage, autoSendMessage])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Animated placeholder effect - cycles through placeholder texts
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % PLACEHOLDER_TEXTS.length)
    }, 4000) // Change placeholder every 4 seconds

    return () => clearInterval(interval)
  }, [PLACEHOLDER_TEXTS.length])

  // Load board list for global chat mode
  useEffect(() => {
    if (chatMode === 'global') {
      loadBoardList()
    }
  }, [chatMode, loadBoardList])

  useEffect(() => {
    if (!context_id) return

    loadChatConversations()

    return () => {
      abortStreamRequest()
    }
  }, [abortStreamRequest, context_id, loadChatConversations])

  // Restore draft from localStorage when conversation or context changes.
  // Only overwrite the input when a saved draft exists — this prevents the
  // auto-conversation selection on board open from clearing text the user
  // typed while conversations were still loading.
  const prevConversationIdRef = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    if (!context_id) return
    const draftKey = `chatDraft_${context_id}_${currentConversationId || 'new'}`
    const isExplicitSwitch = prevConversationIdRef.current !== undefined
      && prevConversationIdRef.current !== currentConversationId
    prevConversationIdRef.current = currentConversationId
    try {
      const draft = localStorage.getItem(draftKey)
      if (draft !== null) {
        // Restore saved draft
        setLocalInputValue(draft)
      } else if (isExplicitSwitch) {
        // User explicitly switched conversations — clear stale input
        setLocalInputValue('')
      }
      // Otherwise (initial auto-select with no draft): keep current input
    } catch (e) {
      console.debug('[ChatInterface] Failed to restore draft:', e)
    }
  }, [context_id, currentConversationId])

  // Save draft to localStorage on input change via setTimeout(0).
  // setTimeout(0) schedules a macrotask — React processes state updates (microtasks) first,
  // so the restore effect's setLocalInputValue cancels this timer before it can delete the draft
  // on mount. Using 0ms instead of a longer debounce prevents draft loss when switching
  // conversations quickly (the save fires before the next conversation's restore cancels it).
  useEffect(() => {
    if (!context_id) return

    const timer = setTimeout(() => {
      const draftKey = `chatDraft_${context_id}_${currentConversationId || 'new'}`
      try {
        if (localInputValue.trim()) {
          localStorage.setItem(draftKey, localInputValue)
        } else {
          localStorage.removeItem(draftKey)
        }
      } catch (e) {
        console.debug('[ChatInterface] Failed to save draft:', e)
      }
    }, 0)

    return () => clearTimeout(timer)
  }, [localInputValue, context_id, currentConversationId])

  useEffect(() => {
    if (speechQueue.length > 0 && audioOutEnabled) {
      processQueue()
    }
  }, [speechQueue, audioOutEnabled, processQueue])

  useEffect(() => {
    return () => {
      abortStreamRequest()
      if (currentMessageIdRef.current) {
        unregisterStopHandler(currentMessageIdRef.current)
      }
      // Reset loading state on unmount so it doesn't stay stuck in the
      // global store when the board is closed mid-stream.
      setIsChatLoading(false)
    }
  }, [abortStreamRequest, unregisterStopHandler, setIsChatLoading])

  useEffect(() => {
    if (chat_id) {
      setDocumentSelected(chat_id)
      setContextId(chat_id)
    } else if (context_id && location.pathname.includes('/chat')) {
      setDocumentSelected(context_id)
      navigate(`/chat/${context_id}`)
    }
  }, [chat_id, setDocumentSelected, setContextId, context_id, navigate])

  // Map frontend agent IDs to chat agent identifiers
  const mapAgentIdToChatAgent = useCallback(
    (agentId: string | undefined): string | null => {
      if (!agentId) return null
      const mapping: Record<string, string> = {
        'plant-support': 'plant_support_engineer',
        'chemical-agent': 'chemical_engineer',
        'mechanical-agent': 'mechanical_engineer',
        'electrical-agent': 'electrical_engineer',
      }
      return mapping[agentId.toLowerCase()] || null
    },
    []
  )

  // Initialize activeAgent from selectedAgent when navigating from main page to new chat.
  // Board (canvas) chat always starts with the default plant_support_engineer agent.
  useEffect(() => {
    if (chatMode !== 'canvas' && !currentConversationId && selectedAgent?.id) {
      const chatAgent = mapAgentIdToChatAgent(selectedAgent.id)
      if (chatAgent) {
        setActiveAgent(chatAgent)
      }
    }
  }, [
    chatMode,
    selectedAgent,
    currentConversationId,
    setActiveAgent,
    mapAgentIdToChatAgent,
  ])

  const onGlobalDocSelect = (value: string) => {
    navigate(`/chat/${value}`)
    setDocumentSelected(value)
    setChatConversations([])
    setCurrentConversationId(null)
    setChatMessages([])
    setResponseVersions(new Map())
    setContextId(value)
    // Reset to plant_support_engineer or use selectedAgent if available when switching workspaces
    const chatAgent = mapAgentIdToChatAgent(selectedAgent?.id)
    setActiveAgent(chatAgent || 'plant_support_engineer')
  }

  const handleStopResponse = useCallback(() => {
    // Debounce repeated Stop gestures.
    const now = Date.now()
    const DEBOUNCE_MS = 500  // 500ms debounce window
    if (now - lastCancelTimestampRef.current < DEBOUNCE_MS) {
      return
    }
    lastCancelTimestampRef.current = now

    // Always do audio cleanup first (independent of session state)
    if (audioOutEnabled && currentPlayer && !currentPlayer.isClosed) {
      flushSpeech()
      setAudioOutEnabled(false)
      setAISpeaking(false)
    }

    // Capture the active request and message before aborting.
    const streamAbortController = streamAbortControllerRef.current
    const messageIdToStop = currentMessageIdRef.current

    // Abort the active streaming request if one exists.
    if (streamAbortController) {
      streamAbortController.abort()
      if (streamAbortControllerRef.current === streamAbortController) {
        streamAbortControllerRef.current = null
      }
    }

    // Always clear loading state so the UI isn't stuck
    setIsChatLoading(false)

    // Change reasoning_path status to 'stopped' when stop button is pressed
    // Use flushSync to force synchronous update for immediate UI feedback
    if (messageIdToStop) {
      flushSync(() => {
        setChatMessages((prevMessages) =>
          prevMessages.map((msg: Message) => {
            if (msg.id === messageIdToStop && msg.reasoning_path) {
              // Update status to 'stopped' to show in the "Thinking" panel
              return {
                ...msg,
                reasoning_path: finalizeReasoningPathState(msg.reasoning_path, 'stopped'),
              }
            }
            return msg
          })
        )
      })
    }

    // Native chat stops through the active request's AbortSignal. Python no
    // longer registers these sessions, so there is no second cancel request.
    if (messageIdToStop) unregisterStopHandler(messageIdToStop)
  }, [
    audioOutEnabled,
    currentPlayer,
    flushSpeech,
    stopReply,
    unregisterStopHandler,
    setAudioOutEnabled,
    setAISpeaking,
    setIsChatLoading,
    setChatMessages,
  ])

  const handleNewMessage = useCallback(
    (message: Message) => {
      if (message.role === ROLES.assistant && message.content.trim()) {
        if (audioOutEnabled) {
          addToSpeechQueue(message.content)
          setAISpeaking(true)
        }
        if (isRecording) {
          setIsRecording(false)
        }
      }
    },
    [
      audioOutEnabled,
      addToSpeechQueue,
      setAISpeaking,
      isRecording,
      setIsRecording,
    ]
  )

  const updateMessages = useCallback(
    (newMessage: Message) => {
      // Handle user message ID sync event (backend sends actual DB ID as first event)
      if (newMessage.role === 'user' && newMessage.id && !newMessage.content) {
        // Update the last user message's ID to match the database
        setChatMessages((prevMessages) => {
          // Find the last user message (most recently added)
          for (let i = prevMessages.length - 1; i >= 0; i--) {
            if (prevMessages[i].role === 'user') {
              return prevMessages.map((msg, index) =>
                index === i ? { ...msg, id: newMessage.id } : msg
              )
            }
          }
          return prevMessages
        })
        return // Don't process further - this was just an ID sync
      }

      handleNewMessage(newMessage)

      // Update active agent when assistant message includes agent_used,
      // but only if the backend returned a user-facing agent (not an internal routing agent).
      // This prevents the orchestrator (plant_support_engineer) from overriding the user's
      // explicit agent selection when it routes through sub-agents internally.
      if (newMessage.role === ROLES.assistant && newMessage.agent_used) {
        const normalizedBackendAgent = normalizeAgentName(newMessage.agent_used)
        const isUserFacingAgent = normalizedBackendAgent in AGENT_ALIASES

        if (isUserFacingAgent) {
          setActiveAgent(newMessage.agent_used)

          // Also update the conversation's active_agent in the chatConversations array
          // so it persists when user returns to this conversation
          if (currentConversationId) {
            setChatConversations((prevConversations) =>
              prevConversations.map((conv) =>
                conv.id === currentConversationId
                  ? { ...conv, active_agent: newMessage.agent_used || 'plant_support_engineer' }
                  : conv
              )
            )
          }
        }
      }

      // rAF buffering already paces SSE bursts. Let React batch these updates
      // instead of forcing a synchronous commit for every buffered frame.
      setChatMessages((prevMessages) => {
        const lastMessageIndex = prevMessages.length - 1
        const isReplace = newMessage.stream_action === 'replace'

        // Detect agent from the last user message's @mention - this takes priority!
        const getAgentFromUserMessage = () => {
          for (let i = prevMessages.length - 1; i >= 0; i--) {
            if (prevMessages[i].role === 'user') {
              const mentionMatch = prevMessages[i].content.match(/(?<!\w)@(ai-plant-support-engineer|ai-chemical-engineer|ai-mechanical-engineer|ai-electrical-engineer|aice|max|pam|ella)\b/i)
              if (mentionMatch) {
                const rawMention = mentionMatch[1].toLowerCase()
                // Map long-form mentions, then use normalizeAgentName for legacy names
                const mentionToAgent: Record<string, string> = {
                  'ai-plant-support-engineer': AgentId.PLANT_SUPPORT,
                  'ai-chemical-engineer': AgentId.CHEMICAL,
                  'ai-mechanical-engineer': AgentId.MECHANICAL,
                  'ai-electrical-engineer': AgentId.ELECTRICAL,
                }
                return mentionToAgent[rawMention] || normalizeAgentName(rawMention)
              }
              break
            }
          }
          return null
        }

        // The user's explicit @mention wins the displayed persona: the unified
        // backend stamps every response `plant_support_engineer`, so deferring
        // to it shows the plant-support avatar even when another agent was
        // called. Fall back to the backend value, then the active agent.
        const userRequestedAgent = getAgentFromUserMessage()
        const effectiveAgent = resolveResponseDisplayAgent({
          userRequestedAgent,
          backendAgentUsed: newMessage.agent_used,
          fallbackAgent: activeAgent,
        })
        const assistantMessageIndex =
          newMessage.role === ROLES.assistant && newMessage.id
            ? prevMessages.findIndex(
                (msg) =>
                  msg.role === ROLES.assistant &&
                  msg.id === newMessage.id
              )
            : -1
        const fallbackAssistantIndex =
          assistantMessageIndex === -1 &&
          newMessage.role === ROLES.assistant &&
          !newMessage.id &&
          lastMessageIndex >= 0 &&
          prevMessages[lastMessageIndex].role === ROLES.assistant
            ? lastMessageIndex
            : -1
        const targetAssistantIndex =
          assistantMessageIndex !== -1
            ? assistantMessageIndex
            : fallbackAssistantIndex

        if (targetAssistantIndex !== -1) {
          return prevMessages.map((msg, index) =>
            index === targetAssistantIndex
              ? {
                  ...msg,
                  id: newMessage.id || msg.id,
                  conversation_id: newMessage.conversation_id || msg.conversation_id,
                  user_message_id:
                    newMessage.user_message_id || msg.user_message_id,
                  content: isReplace ? newMessage.content : msg.content + newMessage.content,
                  thinking:
                    isReplace
                      ? (newMessage.thinking ?? msg.thinking)
                      : (msg.thinking ?? '') + (newMessage.thinking ?? ''),
                  // Keep the "Thinking" panel visible: preserved during streaming,
                  // finalized to 'completed' on stream done (collapsed by default).
                  // Preserve the existing reference when the snapshot hasn't
                  // meaningfully changed so ReasoningPath doesn't re-render
                  // on every text-delta SSE frame.
                  reasoning_path: hasReasoningPathChanged(msg.reasoning_path, newMessage.reasoning_path ?? undefined)
                    ? (newMessage.reasoning_path ?? msg.reasoning_path)
                    : msg.reasoning_path,
                  // Include charts if present in new message
                  charts: newMessage.charts || msg.charts,
                  chart_data: newMessage.chart_data || msg.chart_data,
                  // ENG-2416 §10: fold the latest `sources` snapshot from
                  // the SSE envelope onto the message. Both backends emit
                  // the full array on each frame (not deltas), so prefer
                  // the newest non-empty snapshot; keep the prior one when
                  // a frame omits it (e.g. a pure text-delta frame).
                  sources:
                    newMessage.sources && newMessage.sources.length > 0
                      ? newMessage.sources
                      : msg.sources,
                  workflow_progress: newMessage.workflow_progress ?? msg.workflow_progress,
                  pending_share: newMessage.pending_share ?? msg.pending_share,
                  // Explicit @mention wins (see effectiveAgent above); else the
                  // backend value, then whatever was already stamped.
                  agent_used: resolveResponseDisplayAgent({
                    userRequestedAgent,
                    backendAgentUsed: newMessage.agent_used,
                    fallbackAgent: msg.agent_used || effectiveAgent,
                  }),
                  // AGNT-685: stamp correlation_id from any frame that
                  // carries one (the streamMessage is mutated to add it
                  // when the response header arrives), prefer the
                  // already-set value on the existing msg if present.
                  correlation_id: msg.correlation_id || newMessage.correlation_id,
                }
              : msg
          )
        }

        // For new messages, user's @mention takes priority
        return [...prevMessages, {
          ...newMessage,
          agent_used: effectiveAgent
        }]
      })
    },
    [
      handleNewMessage,
      currentConversationId,
      setActiveAgent,
      setChatConversations,
      setChatMessages,
      activeAgent,
    ]
  )

  const syncLatestAssistantMessage = useCallback(
    async (
      conversationId: string,
      responseDurationMs?: number,
      correlationId?: string | null
    ): Promise<AssistantHistorySyncResult> => {
      let syncedAssistantForTurn = false
      let syncResult: AssistantHistorySyncResult = {
        status: 'missing',
        assistantId: null,
      }

      try {
        const freshMessages = await apiService.getConversationMessages(conversationId)
        if (!freshMessages || freshMessages.length === 0) return syncResult

        freshMessages.sort(
          (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        )
        // Held in a container object because the assignment happens inside the
        // (synchronously-invoked) setChatMessages updater below. TypeScript's
        // control-flow analysis can't track a closure-captured `let`, so it
        // would otherwise narrow the variable back to its `null` initializer at
        // the read sites. Reading through an object property opts out of that.
        const matchedAssistantForUpdate: { current: Message | null } = {
          current: null,
        }

        setChatMessages((prevMessages) => {
          const lastUserIdx = prevMessages.findLastIndex(
            (msg) =>
              msg.role === ROLES.user &&
              msg.conversation_id === conversationId
          )

          if (lastUserIdx === -1) return prevMessages
          const lastUserMessage = prevMessages[lastUserIdx]

          // Re-anchor the optimistic user bubble's id to the real DB id.
          // The V2 UIMessageStream wire carries no user-id sync frame
          // (unlike the legacy SSE path's `{role:'user', id, content:''}`
          // first event), so the user message keeps the client-generated
          // id it rendered with. A later edit/regenerate then POSTs
          // `/messages/<ghost-id>/edit` and the backend 400s with
          // "Message not found". `freshMessages` is sorted ascending, so
          // the most recent user row is this turn's; we gate on content
          // equality so a transient/empty fetch can't mis-stamp the id.
          const backendUserMessage = [...freshMessages]
            .reverse()
            .find(
              (msg) =>
                msg.role === ROLES.user && msg.content === lastUserMessage.content
            )
          const needsUserReanchor =
            !!backendUserMessage && backendUserMessage.id !== lastUserMessage.id
          const reanchorUserId = (msgs: Message[]): Message[] =>
            needsUserReanchor
              ? msgs.map((msg, index) =>
                  index === lastUserIdx
                    ? { ...msg, id: backendUserMessage!.id }
                    : msg
                )
              : msgs

          let assistantIdx = -1
          for (let i = lastUserIdx + 1; i < prevMessages.length; i++) {
            if (
              prevMessages[i].role === ROLES.assistant &&
              prevMessages[i].conversation_id === conversationId
            ) {
              assistantIdx = i
              break
            }
          }

          const existingAssistant =
            assistantIdx === -1 ? null : prevMessages[assistantIdx]

          const findAssistantForTurn = (userIdx: number) => {
            if (userIdx === -1) return null

            for (let i = userIdx + 1; i < freshMessages.length; i++) {
              const msg = freshMessages[i]
              if (msg.role === ROLES.user) break
              if (msg.role === ROLES.assistant) return msg
            }

            return null
          }

          const findAssistantByUserMessageId = (
            userMessageId?: string | null
          ) => {
            if (!userMessageId) return null

            const linkedAssistant = freshMessages.find(
              (msg) =>
                msg.role === ROLES.assistant &&
                msg.user_message_id === userMessageId
            )

            if (linkedAssistant) return linkedAssistant

            const backendUserIdx = freshMessages.findLastIndex(
              (msg) =>
                msg.role === ROLES.user &&
                msg.id === userMessageId
            )

            return findAssistantForTurn(backendUserIdx)
          }

          const assistantForCurrentTurn = findAssistantByUserMessageId(
            lastUserMessage.id
          )

          // Recover the current turn when the empty user-ID sync SSE event is missed
          // but the assistant stream already told us which backend user turn it belongs to.
          const linkedAssistantForCurrentTurn = findAssistantByUserMessageId(
            existingAssistant?.user_message_id
          )

          const idMatchedAssistant = existingAssistant?.id
            ? freshMessages.find(
                (msg) =>
                  msg.role === ROLES.assistant &&
                  msg.id === existingAssistant.id
              )
            : null

          const matchedAssistant =
            idMatchedAssistant ??
            assistantForCurrentTurn ??
            linkedAssistantForCurrentTurn ??
            null

          syncResult = {
            status: getAssistantHistorySyncStatus(matchedAssistant),
            assistantId: matchedAssistant?.id ?? existingAssistant?.id ?? null,
          }

          if (!matchedAssistant) {
            // Still re-anchor the user id even when the assistant isn't
            // matched yet — the user row is persisted regardless, and the
            // edit affordance targets the user message.
            return reanchorUserId(prevMessages)
          }

          // Do not overwrite the current turn with an older assistant from a previous turn.
          if (
            !idMatchedAssistant &&
            assistantForCurrentTurn &&
            existingAssistant?.id &&
            assistantForCurrentTurn.id !== existingAssistant.id
          ) {
            return reanchorUserId(prevMessages)
          }

          if (
            !idMatchedAssistant &&
            !assistantForCurrentTurn &&
            linkedAssistantForCurrentTurn &&
            existingAssistant?.id &&
            linkedAssistantForCurrentTurn.id !== existingAssistant.id
          ) {
            return reanchorUserId(prevMessages)
          }

          matchedAssistantForUpdate.current = matchedAssistant
          syncedAssistantForTurn = true

          const syncedAssistant: Message = existingAssistant
            ? {
                ...existingAssistant,
                ...matchedAssistant,
                feedback: existingAssistant.feedback,
                rating: existingAssistant.rating,
                // Persona is a frontend display concept; the persisted DB row
                // always carries the generic plant_support_engineer. Keep the
                // agent resolved during streaming (the user's @mention) so the
                // avatar doesn't revert to plant-support on post-stream sync.
                agent_used: existingAssistant.agent_used || matchedAssistant.agent_used,
                responseDurationMs:
                  responseDurationMs ?? existingAssistant.responseDurationMs,
                // AGNT-685: keep correlation_id alive across recovery
                // sync. Persisted DB messages don't carry it (in-memory
                // only), but the streaming or recovery caller has the
                // value from the response header. Prefer existing →
                // caller-passed → fall through to undefined.
                correlation_id:
                  existingAssistant.correlation_id ?? correlationId ?? undefined,
                reasoning_path: matchedAssistant.reasoning_path
                  ? (
                    existingAssistant.reasoning_path?.status === 'completed' ||
                    existingAssistant.reasoning_path?.status === 'error' ||
                    existingAssistant.reasoning_path?.status === 'stopped'
                  )
                    ? finalizeReasoningPathState(
                        matchedAssistant.reasoning_path,
                        existingAssistant.reasoning_path.status
                      )
                    : matchedAssistant.reasoning_path
                  : existingAssistant.reasoning_path,
              }
            : {
                ...matchedAssistant,
                responseDurationMs,
                // AGNT-685: stamp correlation_id when creating a fresh
                // assistant from the DB-fetched message (no streaming
                // version existed for this turn).
                correlation_id: correlationId ?? undefined,
              }

          const base = reanchorUserId(prevMessages)

          if (assistantIdx === -1) {
            return [
              ...base.slice(0, lastUserIdx + 1),
              syncedAssistant,
              ...base.slice(lastUserIdx + 1),
            ]
          }

          return base.map((msg, index) =>
            index === assistantIdx ? syncedAssistant : msg
          )
        })

        const matchedAgentUsed = matchedAssistantForUpdate.current?.agent_used
        if (matchedAgentUsed) {
          const normalizedSyncAgent = normalizeAgentName(matchedAgentUsed)
          const isSyncAgentUserFacing = normalizedSyncAgent in AGENT_ALIASES

          if (isSyncAgentUserFacing) {
            setActiveAgent(matchedAgentUsed)
            setChatConversations((prevConversations) =>
              prevConversations.map((conv) =>
                conv.id === conversationId
                  ? {
                      ...conv,
                      active_agent: matchedAgentUsed || 'plant_support_engineer',
                    }
                  : conv
              )
            )
          }
        }
      } catch (error) {
        console.debug(
          '[ChatInterface] Failed to sync latest assistant message:',
          error
        )
        return syncResult
      }

      return syncedAssistantForTurn
        ? syncResult
        : {
            status: 'missing',
            assistantId: null,
          }
    },
    [setActiveAgent, setChatConversations, setChatMessages]
  )

  // User-id-only variant of the re-anchor inside syncLatestAssistantMessage,
  // for turns that end in an explicit `error`/`cancelled` terminal state.
  // Those turns skip the full history sync on purpose (the DB assistant row
  // may be missing, or ahead of what the user saw when they hit stop), but
  // the user row was persisted when the turn started — so the optimistic
  // client uuid must still be swapped for the DB id. Without this, the very
  // next regenerate/edit POSTs `/messages/<ghost-id>/edit` and the backend
  // 400s with "Message not found" — and error turns are exactly the ones
  // users regenerate. Same content-equality gate as the full sync so a
  // transient/empty fetch can't mis-stamp the id.
  const reanchorLatestUserMessageId = useCallback(
    async (conversationId: string): Promise<void> => {
      try {
        const freshMessages = await apiService.getConversationMessages(conversationId)
        if (!freshMessages || freshMessages.length === 0) return

        freshMessages.sort(
          (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        )

        setChatMessages((prevMessages) => {
          const lastUserIdx = prevMessages.findLastIndex(
            (msg) =>
              msg.role === ROLES.user &&
              msg.conversation_id === conversationId
          )
          if (lastUserIdx === -1) return prevMessages
          const lastUserMessage = prevMessages[lastUserIdx]

          const backendUserMessage = [...freshMessages]
            .reverse()
            .find(
              (msg) =>
                msg.role === ROLES.user && msg.content === lastUserMessage.content
            )
          if (!backendUserMessage || backendUserMessage.id === lastUserMessage.id) {
            return prevMessages
          }

          return prevMessages.map((msg, index) =>
            index === lastUserIdx ? { ...msg, id: backendUserMessage.id } : msg
          )
        })
      } catch (error) {
        console.debug(
          '[ChatInterface] Failed to re-anchor user message id after non-completed turn:',
          error
        )
      }
    },
    [setChatMessages]
  )

  const recoverAssistantAfterUnexpectedStreamEnd = useCallback(
    async ({
      conversationId,
      messageId,
      responseDurationMs,
      streamTimedOut,
      correlationId,
    }: {
      conversationId: string
      messageId: string
      responseDurationMs: number
      streamTimedOut: boolean
      correlationId?: string | null
    }): Promise<AssistantHistorySyncResult> => {
      console.warn(
        '[ChatInterface] Stream ended without terminal event; starting recovery sync.',
        {
          conversationId,
          messageId,
          streamTimedOut,
          retryDelaysMs: STREAM_RECOVERY_RETRY_DELAYS_MS,
        }
      )

      let lastResult: AssistantHistorySyncResult = {
        status: 'missing',
        assistantId: null,
      }

      for (let attempt = 0; attempt < STREAM_RECOVERY_RETRY_DELAYS_MS.length; attempt += 1) {
        const delayMs = STREAM_RECOVERY_RETRY_DELAYS_MS[attempt]
        if (delayMs > 0) {
          await sleep(delayMs)
        }

        if (currentMessageIdRef.current !== messageId) {
          console.info(
            '[ChatInterface] Stopping recovery sync because a newer turn is active.',
            {
              conversationId,
              messageId,
              attempt: attempt + 1,
            }
          )
          return {
            status: 'interrupted',
            assistantId: null,
          }
        }

        lastResult = await syncLatestAssistantMessage(conversationId, responseDurationMs, correlationId)

        console.info('[ChatInterface] Recovery sync attempt finished.', {
          conversationId,
          messageId,
          attempt: attempt + 1,
          totalAttempts: STREAM_RECOVERY_RETRY_DELAYS_MS.length,
          delayMs,
          status: lastResult.status,
          assistantId: lastResult.assistantId,
        })

        if (
          lastResult.status === 'completed' ||
          lastResult.status === 'error' ||
          lastResult.status === 'stopped'
        ) {
          return lastResult
        }
      }

      console.warn(
        '[ChatInterface] Recovery sync exhausted without a terminal assistant state.',
        {
          conversationId,
          messageId,
          finalStatus: lastResult.status,
          assistantId: lastResult.assistantId,
        }
      )

      return lastResult
    },
    [syncLatestAssistantMessage]
  )

  const streamChatResponse = useCallback(
    async ({
      conversationId,
      messageId,
      input,
      regenerate = false,
      streamStartTime,
    }: {
      conversationId: string
      messageId: string
      input: string
      regenerate?: boolean
      streamStartTime: number
    }) => {
      const abortController = new AbortController()
      streamAbortControllerRef.current = abortController
      registerStopHandler(messageId, handleStopResponse)

      let streamSettled = false
      let currentAssistantMessageId: string | null = null
      let explicitTerminalStatus: AssistantStreamTerminalStatus | null = null
      let explicitTerminalEvent: AssistantStreamTerminalEvent | null = null
      let streamAborted = false
      let streamTimedOut = false
      // AGNT-685: capture x-correlation-id from the response header.
      // Hoisted to the streamChatResponse outer scope so `settleStream`
      // (which runs after streaming finishes / aborts / errors) can
      // pass it to the recovery sync.
      let responseCorrelationId: string | null = null

      const finalizeReasoningPath = (
        assistantMessageId: string | null,
        finalStatus: 'completed' | 'error' | 'stopped'
      ) => {
        setChatMessages((prevMessages) => {
          const targetAssistantIdx = assistantMessageId
            ? prevMessages.findIndex(
                (m) =>
                  m.role === 'assistant' &&
                  m.id === assistantMessageId
              )
            : prevMessages.findLastIndex((m) => m.role === 'assistant')
          if (targetAssistantIdx === -1) return prevMessages
          const msg = prevMessages[targetAssistantIdx]
          if (!msg.reasoning_path || msg.reasoning_path.status !== 'in_progress') {
            return prevMessages
          }
          return prevMessages.map((m, i) =>
            i === targetAssistantIdx
              ? {
                  ...m,
                  reasoning_path: finalizeReasoningPathState(
                    m.reasoning_path,
                    finalStatus
                  ),
                }
              : m
          )
        })
      }

      const setCompletionTimestamp = (
        assistantMessageId: string | null,
        completionTimestamp?: string
      ) => {
        const responseDurationMs = Date.now() - streamStartTime
        setChatMessages((prevMessages) => {
          const targetAssistantIdx = assistantMessageId
            ? prevMessages.findIndex(
                (m) =>
                  m.role === 'assistant' &&
                  m.id === assistantMessageId
              )
            : prevMessages.findLastIndex((m) => m.role === 'assistant')
          if (targetAssistantIdx === -1) return prevMessages
          return prevMessages.map((m, i) =>
            i === targetAssistantIdx
              ? {
                  ...m,
                  timestamp: completionTimestamp || new Date().toISOString(),
                  responseDurationMs,
                }
              : m
          )
        })
      }

      const settleStream = async () => {
        if (streamSettled) return
        streamSettled = true

        if (streamAbortControllerRef.current === abortController) {
          streamAbortControllerRef.current = null
        }

        const isCurrentTurnActive = () => currentMessageIdRef.current === messageId

        if (isCurrentTurnActive()) {
          const responseDurationMs = Date.now() - streamStartTime

          if (explicitTerminalStatus === 'completed') {
            finalizeReasoningPath(currentAssistantMessageId, 'completed')
            setCompletionTimestamp(
              currentAssistantMessageId,
              explicitTerminalEvent?.timestamp
            )
            void syncLatestAssistantMessage(conversationId, responseDurationMs)
          } else if (explicitTerminalStatus === 'cancelled' || streamAborted) {
            finalizeReasoningPath(currentAssistantMessageId, 'stopped')
            void reanchorLatestUserMessageId(conversationId)
          } else if (explicitTerminalStatus === 'error') {
            finalizeReasoningPath(currentAssistantMessageId, 'error')
            void reanchorLatestUserMessageId(conversationId)
          } else {
            if (streamTimedOut) {
              console.warn(
                '[ChatInterface] Stream timed out without terminal event; attempting recovery sync'
              )
            }
            const recoveryResult = await recoverAssistantAfterUnexpectedStreamEnd({
              conversationId,
              messageId,
              responseDurationMs,
              streamTimedOut,
              correlationId: responseCorrelationId,
            })

            if (!isCurrentTurnActive()) {
              // FS-588: only skip the loading reset when a successor
              // stream actually exists to own it — otherwise the stop
              // button stays pinned with nothing left to clear it.
              if (streamAbortControllerRef.current === null) {
                setIsChatLoading(false)
                clearToolProgress()
              }
              unregisterStopHandler(messageId)
              return
            }

            const recoveredAssistantMessageId =
              recoveryResult.assistantId ?? currentAssistantMessageId

            if (recoveryResult.status === 'completed') {
              finalizeReasoningPath(recoveredAssistantMessageId, 'completed')
            } else if (recoveryResult.status === 'stopped') {
              finalizeReasoningPath(recoveredAssistantMessageId, 'stopped')
            } else {
              finalizeReasoningPath(recoveredAssistantMessageId, 'error')
            }
          }

          setIsChatLoading(false)
          // W16 / TIME-198: defensive clear at stream-end. If the
          // backend forgot to send `completed`/`failed`, or the stream
          // aborted mid-flight, drop any stale tool-progress line —
          // and cancel any in-flight failed-hold timer — so neither
          // outlives the turn.
          clearToolProgress()
          currentMessageIdRef.current = null
        } else if (streamAbortControllerRef.current === null) {
          // FS-588: this turn was superseded, but no newer stream is
          // registered — nothing else will reset the loading flag, so
          // do it here rather than leaving the stop button pinned.
          setIsChatLoading(false)
          clearToolProgress()
        }

        unregisterStopHandler(messageId)
      }

      try {
        const response = await apiService.sendMessage(
          conversationId,
          input,
          regenerate,
          undefined,
          abortController.signal,
          // Read from the store to avoid a stale closure (like activeAgent).
          useAppStore.getState().entireDataCatalog
        )

        // Capture the backend's correlation id from the response header.
        // AGNT-684 binds Langfuse trace ids to this value, so the chat
        // UI can deep-link to /kg-trace/:correlationId for forensics.
        // Stamped on the assistant message via streamMessage mutation
        // (live path) and via syncedAssistant (recovery path) so the
        // 🔍 link in MessageBubble has a stable target either way.
        responseCorrelationId = response.headers.get('x-correlation-id')

        // Response-formatting design — when the NestJS gateway emits
        // Vercel UIMessageStream v1 frames (driven by `formatting_v2`
        // PostHog flag + our request-side parts-protocol header), the
        // payload shape changes from `{role, content, thinking, ...}`
        // to `{type: 'text-delta', delta: '...'}` etc. We accumulate
        // those frames into running text/reasoning buffers and feed a
        // V1-shaped synthetic frame into `updateMessages` so the
        // existing render pipeline doesn't need to know about the new
        // wire format. Tool/data-* frames are out of scope for this
        // minimum integration — they're observability-only here and
        // are persisted server-side regardless.
        const isV2Protocol =
          response.headers.get(VERCEL_AI_STREAM_HEADER) === VERCEL_AI_STREAM_VERSION
        let v2AccText = ''
        let v2AccReasoning = ''
        let v2AssistantMessageId: string | null = null
        // Latest structured `reasoning_path` snapshot from the backend.
        // The V2 emitter writes a `data-kg-reasoning-path` frame at
        // each tool boundary so the step-list UI keeps working on
        // surfaces that haven't moved to the V3 prose accordion.
        let v2ReasoningPath: Message['reasoning_path'] | undefined
        // Latest historian chart payload from the backend's
        // `data-historian-chart` frame, fed into the synthetic so the
        // existing TimeseriesCharts renders it live.
        let v2ChartData: Message['chart_data']
        // Latest structured citations from the backend's `data-kg-sources`
        // snapshot frame (sources analogue of `data-kg-reasoning-path`).
        // Folded into the synthetic so MessageBubble's SourcesList renders
        // for kg-agent answers (P&ID / timeseries / doc) under V2, matching
        // the V1 SSE envelope path. The snapshot is already MessageSource[].
        let v2AccSources: Message['sources']
        let v2WorkflowProgress: Message['workflow_progress']
        // FS-663: a share the agent prepared this turn, awaiting the user's confirm.
        let v2PendingShare: Message['pending_share']

        for await (const event of readSSEMessages(response, {
          inactivityTimeoutMs: STREAM_INACTIVITY_TIMEOUT_MS,
        })) {
          if (event.event === 'done' || event.data === '[DONE]') {
            // V2 protocol terminates with `data: [DONE]` only — it
            // never emits the V1 `assistant_message_end` event that
            // sets `explicitTerminalStatus`. Without this marker the
            // post-stream branch falls into the recovery-sync path,
            // which polls for a terminal state that will never arrive
            // and leaves the typing indicator pinned until timeout.
            // Stamp 'completed' on a clean V2 [DONE] so the cleanup
            // (`finalizeReasoningPath('completed')` + UI state reset)
            // runs immediately.
            if (isV2Protocol && !explicitTerminalStatus) {
              explicitTerminalStatus = 'completed'
              currentAssistantMessageId =
                v2AssistantMessageId ?? currentAssistantMessageId
            }
            break
          }

          if (!event.data) {
            continue
          }

          if (isV2Protocol) {
            // V2 protocol: parse Vercel UIMessageStream frames into a
            // V1-shaped synthetic so the rest of the chat surface
            // (updateMessages → message store → MessageBubble) is
            // protocol-agnostic. Only `text-*` and `reasoning-*` frames
            // contribute to user-visible content here; the rest are
            // dropped (already persisted by the backend).
            let frame: {
              type?: string
              delta?: string
              id?: string
              snapshot?: Message['reasoning_path']
              chart_data?: Message['chart_data']
              sources?: Message['sources']
            }
            try {
              frame = JSON.parse(event.data)
            } catch {
              continue
            }
            if (frame.type === 'text-delta' && typeof frame.delta === 'string') {
              v2AccText += frame.delta
            } else if (
              frame.type === 'reasoning-delta' &&
              typeof frame.delta === 'string'
            ) {
              v2AccReasoning += frame.delta
            } else if (
              frame.type === 'data-kg-reasoning-path' &&
              frame.snapshot
            ) {
              // Step-list snapshot from the V2 emitter — feed it
              // straight into the synthetic so the existing
              // `ReasoningPath` accordion in MessageBubble renders.
              v2ReasoningPath = frame.snapshot
            } else if (
              frame.type === 'data-historian-chart' &&
              Array.isArray(frame.chart_data)
            ) {
              v2ChartData = frame.chart_data
            } else if (
              frame.type === 'data-kg-sources' &&
              Array.isArray(frame.sources)
            ) {
              // Citations snapshot from the V2 emitter — fold into the
              // synthetic so MessageBubble's SourcesList renders. Snapshot
              // replaces (not appends): the backend re-emits the full list.
              v2AccSources = frame.sources
            } else if (frame.type === 'data-workflow-progress') {
              // The UI-message-stream emitter wraps data-parts as
              // `{ type, id, data: <payload> }` (see writeDataPartFrameWithValidated),
              // so the progress fields live under `data` — NOT at the top level
              // like text-delta/reasoning-delta. Spreading the whole frame would
              // hand the schema `id`/`data` instead of `phases`/`status` and the
              // parse would silently fail. Unwrap `data` to match the legacy path.
              const progress = WorkflowProgressPartSchema.safeParse({
                type: 'data-workflow-progress',
                ...((frame as { data?: Record<string, unknown> }).data ?? {}),
              })
              if (progress.success) {
                v2WorkflowProgress = progress.data
              }
            } else if (frame.type === 'data-pending-share') {
              // FS-663: same `{ type, id, data }` unwrap as workflow-progress.
              const share = PendingSharePartSchema.safeParse({
                type: 'data-pending-share',
                ...((frame as { data?: Record<string, unknown> }).data ?? {}),
              })
              if (share.success) {
                v2PendingShare = share.data
              }
            } else {
              // text-start/end, reasoning-start/end, tool-*, other
              // data-*, start/finish-step, finish, error —
              // non-contributing in this minimum-integration path.
              continue
            }
            if (!v2AssistantMessageId) {
              v2AssistantMessageId = uuidv4()
              currentAssistantMessageId = v2AssistantMessageId
            }
            // Synthesize a V1-shaped frame keyed to the synthetic
            // assistant id. `stream_action: 'replace'` so the V1
            // accumulator in updateMessages overwrites rather than
            // appends — our buffer IS the full accumulated text.
            const synthetic = {
              id: v2AssistantMessageId,
              role: ROLES.assistant,
              content: v2AccText,
              thinking: v2AccReasoning || undefined,
              reasoning_path: v2ReasoningPath,
              chart_data: v2ChartData,
              sources: v2AccSources,
              workflow_progress: v2WorkflowProgress,
              pending_share: v2PendingShare,
              correlation_id: responseCorrelationId ?? undefined,
              // FS-490: V2 synthetic frames don't carry conversation_id
              // from the backend (V1 SSE frames do). Without this the
              // assistant message lands in the store with conversation_id
              // undefined, and AnswerActions submits `/conversations/undefined/...`
              // which fails Pydantic UUID validation.
              conversation_id: conversationId,
              stream_action: 'replace' as const,
              timestamp: new Date().toISOString(),
            } as unknown as Message
            // Dedupe in the buffer — every push carries the full
            // cumulative text (replace semantics), so keeping prior
            // un-flushed synthetics around just makes the RAF call
            // `flushSync(setChatMessages)` N times per frame with
            // progressively larger payloads. With 100s of deltas
            // that locks the main thread. One entry per assistant id
            // is enough; the latest wins on flush.
            const buf = streamFrameBufferRef.current as Array<{ id?: string; role?: string }>
            let replaced = false
            for (let i = 0; i < buf.length; i++) {
              const f = buf[i]
              if (f.id === v2AssistantMessageId && f.role === ROLES.assistant) {
                buf[i] = synthetic as unknown as typeof f
                replaced = true
                break
              }
            }
            if (!replaced) {
              streamFrameBufferRef.current.push(synthetic)
            }
            if (streamRafIdRef.current === null) {
              streamRafIdRef.current = requestAnimationFrame(() => {
                streamRafIdRef.current = null
                const frames = streamFrameBufferRef.current.splice(0)
                frames.forEach((f) => updateMessages(f))
              })
            }
            continue
          }

          let parsedEvent: Message & AssistantStreamTerminalEvent & {
            debug_info?: {
              text?: string
              token_usage?: unknown
            }
          }

          try {
            parsedEvent = JSON.parse(event.data)
          } catch {
            continue
          }

          if (
            event.event === ASSISTANT_MESSAGE_END_EVENT ||
            event.event === ASSISTANT_MESSAGE_ERROR_EVENT ||
            event.event === ASSISTANT_MESSAGE_CANCELLED_EVENT
          ) {
            explicitTerminalStatus = parsedEvent.status
            explicitTerminalEvent = parsedEvent
            currentAssistantMessageId = parsedEvent.id
            continue
          }

          // W16 / TIME-198: tool_progress frame. Bypass updateMessages
          // — these frames carry no assistant content/thinking; routing
          // them through the message merger would either no-op
          // (content==='' && thinking==='') or, worse, accidentally
          // overwrite the active assistant message's id. Closed-enum
          // validation lives in the shared parser so V1 + V2 stay in
          // lock-step.
          const tp = parseToolProgressFrame(parsedEvent)
          if (tp) {
            // Reset any stale failed-hold timer from a prior frame.
            if (failedHoldTimerRef.current) {
              clearTimeout(failedHoldTimerRef.current)
              failedHoldTimerRef.current = null
            }
            if (tp.status === 'completed') {
              setToolProgress(null)
            } else if (tp.status === 'failed') {
              // Brief hold so the red-dot styling actually paints
              // before unmount — Abdullah / Greptile flicker concern.
              setToolProgress(tp)
              failedHoldTimerRef.current = setTimeout(() => {
                setToolProgress(null)
                failedHoldTimerRef.current = null
              }, FAILED_HOLD_MS)
            } else {
              setToolProgress(tp)
            }
            continue
          }

          const eventType = (parsedEvent as { type?: unknown }).type
          if (eventType === 'data-workflow-progress') {
            const progress = WorkflowProgressPartSchema.safeParse({
              type: 'data-workflow-progress',
              ...((parsedEvent as { data?: Record<string, unknown> }).data ?? {}),
            })
            if (progress.success) {
              parsedEvent.workflow_progress = progress.data
            }
          }
          // FS-663: the native agent writes this frame with the part inline
          // (`pending_share`), not wrapped under `data` — accept either.
          if (eventType === 'pending_share' || eventType === 'data-pending-share') {
            const share = PendingSharePartSchema.safeParse(
              (parsedEvent as { pending_share?: unknown }).pending_share ?? {
                type: 'data-pending-share',
                ...((parsedEvent as { data?: Record<string, unknown> }).data ?? {}),
              },
            )
            if (share.success) {
              parsedEvent.pending_share = share.data
            }
          }

          const streamMessage = parsedEvent

          // Skip backend-internal frames that carry no user-facing role
          // (e.g. `hints_meta` eval-only observability frames). Without
          // this guard they fall through to `updateMessages` which appends
          // them as phantom chat messages with `timestamp: undefined`,
          // producing "Invalid Date" in the message bubble timestamp and
          // a render crash from `highlightAgentMentions(undefined)`.
          if (streamMessage.role !== ROLES.user && streamMessage.role !== ROLES.assistant) {
            continue
          }

          if (
            currentMessageIdRef.current === messageId &&
            streamMessage.role === ROLES.assistant
          ) {
            currentAssistantMessageId = streamMessage.id || currentAssistantMessageId
            setIsChatLoading(true)
            // Stamp the response's correlation id on the assistant
            // message frame. The frame flows through `updateMessages` →
            // `handleNewMessage` and the merge keeps `correlation_id`
            // alive across subsequent stream chunks for the same id.
            if (responseCorrelationId && !streamMessage.correlation_id) {
              streamMessage.correlation_id = responseCorrelationId
            }
          }

          // Buffer the frame and flush once per animation frame so rapid
          // SSE bursts don't block scroll/input events on the main thread.
          streamFrameBufferRef.current.push(streamMessage)
          if (streamRafIdRef.current === null) {
            streamRafIdRef.current = requestAnimationFrame(() => {
              streamRafIdRef.current = null
              const frames = streamFrameBufferRef.current.splice(0)
              frames.forEach((f) => updateMessages(f))
            })
          }
        }
      } catch (error) {
        if (error instanceof SSEInactivityTimeoutError) {
          streamTimedOut = true
          console.error(
            '[ChatInterface] SSE stream timed out waiting for activity:',
            error
          )
        } else if (error instanceof Error && error.name === 'AbortError') {
          streamAborted = true
        } else {
          console.error('Error reading chat stream:', error)
          // Handle HTTP errors (e.g., 403 agent access denied) by showing an
          // error message in the chat and preventing the slow recovery path.
          const httpStatus = (error as { response?: { status?: number } })?.response?.status
          const errorMsg = error instanceof Error ? error.message : 'An unexpected error occurred'
          if (httpStatus && httpStatus >= 400) {
            streamAborted = true
            setChatMessages((prev) => [
              ...prev,
              {
                id: `error-${messageId}`,
                conversation_id: conversationId,
                role: 'assistant' as const,
                content: `⚠️ ${errorMsg}`,
                timestamp: new Date().toISOString(),
                error: errorMsg,
              },
            ])
          }
        }
      } finally {
        // Flush any frames still waiting in the buffer before settling
        // so the final content is visible before recovery sync runs.
        if (streamRafIdRef.current !== null) {
          cancelAnimationFrame(streamRafIdRef.current)
          streamRafIdRef.current = null
        }
        streamFrameBufferRef.current.splice(0).forEach((f) => updateMessages(f))
        await settleStream()
      }
    },
    [
      handleStopResponse,
      registerStopHandler,
      reanchorLatestUserMessageId,
      recoverAssistantAfterUnexpectedStreamEnd,
      setChatMessages,
      setIsChatLoading,
      syncLatestAssistantMessage,
      unregisterStopHandler,
      updateMessages,
    ]
  )

  const startStreamingResponse = useCallback(
    ({
      conversationId,
      messageId,
      input,
      regenerate = false,
    }: {
      conversationId: string
      messageId: string
      input: string
      regenerate?: boolean
    }) => {
      abortStreamRequest()

      // Capture start time after token fetch is handled inside sendMessage,
      // so this measures from request dispatch to stream completion.
      const streamStartTime = Date.now()

      void streamChatResponse({
        conversationId,
        messageId,
        input,
        regenerate,
        streamStartTime,
      })
    },
    [
      abortStreamRequest,
      streamChatResponse,
    ]
  )

  const handleTextareaResize = useCallback(() => {
    const textarea = inputRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
    }
  }, [])

  const handleSendMessage = useCallback(async (messageOverride?: string) => {
    // Annotators have no AI access. The composer is disabled, but the auto-send
    // path (initialMessage → handleSendMessage) can still call this, so
    // short-circuit here before any request or local chat-state mutation. Read
    // the live ref so a role hydrated after this callback was memoized still
    // blocks (no stale closure, no deps widening).
    if (isAnnotatorRef.current) return
    const messageToSend = messageOverride ?? inputValue
    if (!messageToSend.trim()) return

    // FS-611: block new queries while the previous agent response is still
    // streaming. The Send button already swaps to a Stop button while loading,
    // but pressing Enter in the textarea (or a suggested-prompt click) would
    // otherwise fire a second query and leave two concurrent loading states in
    // one thread. Read the ref so the guard stays correct without widening this
    // callback's deps — mirrors handleRegenerate / navigateVersion above.
    if (isChatLoadingRef.current) return

    // Check if workspace selection is required for global chat
    if (chatMode === 'global' && !context_id) {
      toast.error(t('chat:interface.errors.select_workspace'))
      return
    }

    if (!context_id) return

    // Block sends that @-mention an agent the workspace has not enabled.
    // The autocomplete already filters, but users can type mentions manually.
    // FS-574: the store now lists unavailable agents too — only available
    // ones count as enabled here.
    if (storeAgents && storeAgents.length > 0) {
      const enabledStoreIds = storeAgents.filter((a) => a.isAvailable).map((a) => a.id)
      const unavailable = findUnavailableMention(messageToSend, enabledStoreIds)
      if (unavailable) {
        if (unavailable.availableMentions.length > 0) {
          toast.error(
            t('chat:interface.errors.mention_not_enabled', {
              mention: unavailable.mention,
              availableAgents: unavailable.availableMentions.join(', '),
            }),
          )
        } else {
          toast.error(
            t('chat:interface.errors.mention_not_enabled_none_available', {
              mention: unavailable.mention,
            }),
          )
        }
        return
      }
    }

    setIsRecording(false)

    if (audioOutEnabled && isAISpeaking) {
      await handleStopResponse()
    }

    // `userInput` is reassigned below when prepending an @-mention for a
    // non-default activeAgent — must be `let`, not `const` (TS2588 otherwise).
    let userInput = messageToSend
    setLocalInputValue('')

    // Reset textarea height after sending
    if (inputRef.current) {
      inputRef.current.style.height = 'auto'
    }

    // Clear saved draft since the message is being sent
    if (context_id) {
      try {
        const draftKey = `chatDraft_${context_id}_${currentConversationId || 'new'}`
        localStorage.removeItem(draftKey)
      } catch (e) {
        console.debug('[ChatInterface] Failed to clear draft from localStorage:', e)
      }
    }

    // AGENT SWITCHING: Detect @mentions anywhere in the message and update active agent
    // Supports mentions mid-sentence (e.g., "hi @ai-chemical-engineer how are you")
    // Supports both new (@ai-plant-support-engineer, @ai-chemical-engineer, @ai-mechanical-engineer, @ai-electrical-engineer) and legacy (@pam, @aice, @max, @ella) formats
    // Read the latest activeAgent directly from the store to avoid stale closure values
    // (e.g. when auto-send fires shortly after agent was changed from the entry card)
    const currentActiveAgent = useAppStore.getState().activeAgent
    let resolvedAgent = currentActiveAgent
    const mentionMatch = userInput.match(/(?<!\w)@(ai-plant-support-engineer|ai-chemical-engineer|ai-mechanical-engineer|ai-electrical-engineer|aice|max|pam|ella)\b/i)
    if (mentionMatch) {
      const rawMention = mentionMatch[1].toLowerCase()
      // Map long-form mentions, then use normalizeAgentName for legacy names
      const mentionToAgent: Record<string, string> = {
        'ai-plant-support-engineer': AgentId.PLANT_SUPPORT,
        'ai-chemical-engineer': AgentId.CHEMICAL,
        'ai-mechanical-engineer': AgentId.MECHANICAL,
        'ai-electrical-engineer': AgentId.ELECTRICAL,
      }
      const mentionedAgent = mentionToAgent[rawMention] || normalizeAgentName(rawMention)
      // Check for multi-agent mentions (use \b word boundary to match detection logic)
      const allMentions = userInput.match(/(?<!\w)@(ai-plant-support-engineer|ai-chemical-engineer|ai-mechanical-engineer|ai-electrical-engineer|aice|max|pam|ella)\b/gi)
      if (allMentions && allMentions.length > 1) {
        // Multiple agents mentioned - route through plant_support_engineer
        resolvedAgent = 'plant_support_engineer'
        setActiveAgent('plant_support_engineer')
      } else {
        // Single agent mentioned - switch to that agent
        resolvedAgent = mentionedAgent
        setActiveAgent(mentionedAgent)
      }
    } else {
      // If no @mention but activeAgent is set to a non-default agent,
      // prepend the @mention so the backend knows which agent to route to.
      // This is needed in both legacy and agentic loop modes because the
      // sendMessage API doesn't pass the agent as a separate field.
      if (currentActiveAgent && currentActiveAgent !== 'plant_support_engineer') {
        const agentMentionMap: Record<string, string> = {
          'chemical_engineer': '@ai-chemical-engineer',
          'mechanical_engineer': '@ai-mechanical-engineer',
          'electrical_engineer': '@ai-electrical-engineer',
        }
        const mentionName = agentMentionMap[currentActiveAgent]
        if (mentionName) {
          userInput = `${mentionName} ${userInput}`
        }
      }
    }

    trackEvent('chat-message_sent', {
      message_length: messageToSend.trim().length,
      agent_id: resolvedAgent,
      context_type,
      has_attachments: false,
      attachment_count: 0,
    })

    setIsChatLoading(true)

    // FS-588: if the send POST fails, no stream ever starts and nothing
    // else resets the loading flag — the stop (square) button would stay
    // pinned forever. Mirror the edit/regenerate flows: catch, toast,
    // reset. (The streaming path's own finally → settleStream handles
    // everything after the stream starts.)
    try {
      // Capture old message ID before updating ref to ensure old handler gets unregistered
      const oldMessageId = currentMessageIdRef.current

      // Always use normal chat flow - backend will detect timeseries
      const { conversationId, messageId } = await sendUserMessage(
        userInput,
        context_type
      )

      // Unregister old stop handler to prevent memory leak
      if (oldMessageId) {
        unregisterStopHandler(oldMessageId)
      }

      currentMessageIdRef.current = messageId

      // "Add to Chat" context (interim, until first-class file context ships):
      // the displayed user message stays exactly as typed (sendUserMessage above),
      // but the prompt the agent actually receives is suffixed with the attached
      // filenames so it can narrow its document search. Appended (not prepended)
      // so any leading @agent mention stays at the front for routing.
      const contextFileNames = chatContextChips.map((c) => c.name).filter(Boolean)
      const promptInput =
        contextFileNames.length > 0
          ? `${userInput}\n\nFor context, focus your search on these attached files: ${contextFileNames.join(', ')}.`
          : userInput

      startStreamingResponse({ conversationId, messageId, input: promptInput })
    } catch (error) {
      console.error('[ChatInterface] Failed to send message:', error)
      toast.error(t('chat:toasts.send_error', { defaultValue: 'Failed to send message. Please try again.' }))
      setIsChatLoading(false)
    }
  }, [
    inputValue,
    chatMode,
    context_id,
    t,
    audioOutEnabled,
    isAISpeaking,
    handleStopResponse,
    activeAgent,
    setIsChatLoading,
    sendUserMessage,
    context_type,
    startStreamingResponse,
    setIsRecording,
    setLocalInputValue,
    setActiveAgent,
    storeAgents,
    chatContextChips,
  ])

  const handleFeedbackSubmitted = useCallback((messageId: string, rating: number | undefined) => {
    setChatMessages((prevMessages) =>
      prevMessages.map((msg) =>
        msg.id === messageId ? { ...msg, rating } : msg
      )
    )
  }, [setChatMessages])

  const handleRegenerate = useCallback(async (assistantMessageId: string) => {
    const activeConversationId = currentConversationIdRef.current
    if (!activeConversationId || isChatLoadingRef.current) return

    const chatMessages = chatMessagesRef.current
    const assistantIdx = chatMessages.findIndex((msg) => msg.id === assistantMessageId)
    const searchSlice = assistantIdx !== -1 ? chatMessages.slice(0, assistantIdx) : chatMessages
    const userMessageIdx = searchSlice.reduce<number>((last, msg, i) => (msg.role === 'user' ? i : last), -1)
    if (userMessageIdx === -1) return

    const userMessage = chatMessages[userMessageIdx]
    const userMsgId = userMessage.id

    // Archive the current assistant response before removing it
    // Skip archiving empty responses — no point versioning a blank message
    const currentAssistantMsg = assistantIdx !== -1 ? chatMessages[assistantIdx] : undefined
    const hasContent = currentAssistantMsg?.content?.trim().length
    if (currentAssistantMsg && currentAssistantMsg.role === 'assistant' && hasContent) {
      setResponseVersions((prev) => {
        const next = new Map(prev)
        const existing = next.get(userMsgId)
        const entry: V1VersionEntry = existing
          ? { ...existing, responses: [...existing.responses], userMessages: [...(existing.userMessages || [])] }
          : { responses: [], userMessages: [], currentIndex: 0, liveMessage: null, liveUserMessage: null }

        // If user is viewing an old version, push liveMessage back to responses
        if (entry.liveMessage) {
          if (!entry.responses.some((r) => r.id === entry.liveMessage!.id)) {
            entry.responses.push(entry.liveMessage)
            entry.userMessages.push(entry.liveUserMessage ?? userMessage)
          }
          entry.liveMessage = null
          entry.liveUserMessage = null
        }

        // Archive the current displayed message and user question (dedup check)
        if (!entry.responses.some((r) => r.id === currentAssistantMsg.id)) {
          entry.responses.push(currentAssistantMsg)
          entry.userMessages.push(userMessage)
        }

        // Reset to live (new response will be at index = responses.length)
        entry.currentIndex = entry.responses.length
        next.set(userMsgId, entry)
        return next
      })
    }

    setIsChatLoading(true)

    try {
      await apiService.editMessage(activeConversationId, userMessage.id, userMessage.content)
      setChatMessages(chatMessages.slice(0, userMessageIdx + 1))

      const oldMessageId = currentMessageIdRef.current
      const messageId = `regen-${Date.now()}`
      if (oldMessageId) unregisterStopHandler(oldMessageId)
      currentMessageIdRef.current = messageId

      startStreamingResponse({
        conversationId: activeConversationId,
        messageId,
        input: userMessage.content,
        regenerate: true,
      })
    } catch {
      toast.error(t('chat:toasts.regenerate_error'))
      setIsChatLoading(false)
    }
  }, [setChatMessages, setIsChatLoading, startStreamingResponse, t, unregisterStopHandler])

  /** Navigate between response versions for a given user message. */
  const navigateVersion = useCallback(
    (userMsgId: string, direction: 'prev' | 'next') => {
      if (isChatLoadingRef.current) return

      const chatMessages = chatMessagesRef.current
      const responseVersions = responseVersionsRef.current
      const entry = responseVersions.get(userMsgId)
      if (!entry) return

      const totalVersions = entry.responses.length + 1 // archived + live
      const newIndex =
        direction === 'prev'
          ? Math.max(0, entry.currentIndex - 1)
          : Math.min(totalVersions - 1, entry.currentIndex + 1)

      if (newIndex === entry.currentIndex) return

      const userIdx = chatMessages.findIndex((m) => m.id === userMsgId)
      if (userIdx === -1) return
      const assistantIdx = userIdx + 1
      if (assistantIdx >= chatMessages.length || chatMessages[assistantIdx].role !== 'assistant') return

      const currentDisplayedAssistant = chatMessages[assistantIdx]
      const currentDisplayedUser = chatMessages[userIdx]
      const liveIndex = entry.responses.length // index that means "live"

      setResponseVersions((prev) => {
        const next = new Map(prev)
        const existing = next.get(userMsgId)
        if (!existing) return prev // entry was cleared (e.g. conversation changed)
        const e: V1VersionEntry = { ...existing, responses: [...existing.responses], userMessages: [...(existing.userMessages || [])] }

        // Leaving live view → save the current live messages
        if (entry.currentIndex === liveIndex && newIndex !== liveIndex) {
          e.liveMessage = currentDisplayedAssistant
          e.liveUserMessage = currentDisplayedUser
        }

        e.currentIndex = newIndex
        next.set(userMsgId, e)
        return next
      })

      // Determine which messages to show — preserve original IDs so React keys
      // don't change (which would remount motion.div and cause a flash/disappearance).
      // NOTE: The id swap means feedback/download actions will target the live
      // message, not the archived version.  This is a known limitation; a proper
      // fix requires adding a stableKey field to the Message type (separate PR).
      const archivedAssistant =
        newIndex === liveIndex
          ? (entry.liveMessage ?? currentDisplayedAssistant)
          : (entry.responses[newIndex] ?? currentDisplayedAssistant)

      const archivedUser = newIndex === liveIndex
        ? (entry.liveUserMessage ?? currentDisplayedUser)
        : (entry.userMessages?.[newIndex] ?? currentDisplayedUser)

      const assistantToShow = { ...archivedAssistant, id: currentDisplayedAssistant.id }
      const userToShow = { ...archivedUser, id: userMsgId }

      setChatMessages(
        chatMessages.map((msg, idx) => {
          if (idx === userIdx) return userToShow
          if (idx === assistantIdx) return assistantToShow
          return msg
        })
      )
    },
    [setChatMessages]
  )

  /** Get version info for an assistant message. Returns null if no versions exist. */
  const getVersionInfo = useCallback(
    (assistantMessage: Message): VersionInfo | null => {
      if (assistantMessage.role !== 'assistant') return null

      const assistantIdx = chatMessages.findIndex((m) => m.id === assistantMessage.id)
      if (assistantIdx <= 0) return null

      const userMsg = chatMessages[assistantIdx - 1]
      if (!userMsg || userMsg.role !== 'user') return null

      const entry = responseVersions.get(userMsg.id)
      if (!entry || entry.responses.length === 0) return null

      const totalVersions = entry.responses.length + 1
      return {
        currentVersion: entry.currentIndex + 1, // 1-based
        totalVersions,
        userMessageId: userMsg.id,
      }
    },
    [responseVersions, chatMessages]
  )

  /** Get version info for a user message. Returns null if no versions exist. */
  const getUserVersionInfo = useCallback(
    (userMessage: Message): VersionInfo | null => {
      if (userMessage.role !== 'user') return null

      const entry = responseVersions.get(userMessage.id)
      if (!entry || entry.responses.length === 0) return null

      const totalVersions = entry.responses.length + 1
      return {
        currentVersion: entry.currentIndex + 1,
        totalVersions,
        userMessageId: userMessage.id,
      }
    },
    [responseVersions]
  )

  // Edit message handlers
  const handleEditClick = useCallback((message: Message) => {
    setEditingMessage(message)
  }, [])

  const handleEditCancel = useCallback(() => {
    setEditingMessage(null)
  }, [])

  const handleEditConfirm = useCallback(async (newContent: string) => {
    if (!editingMessage || !currentConversationId) return

    // Capture the message ID being edited to prevent race conditions
    const editingMessageId = editingMessage.id

    // Set loading state BEFORE the API call to disable other edit buttons
    setIsChatLoading(true)

    try {
      // Call API to edit the message (this also deletes subsequent messages in DB)
      await apiService.editMessage(
        currentConversationId,
        editingMessageId,
        newContent
      )

      // Find the index of the edited message
      const messageIndex = chatMessages.findIndex(m => m.id === editingMessageId)
      if (messageIndex === -1) {
        // Message not found in local state - this shouldn't happen but handle gracefully
        toast.error(t('chat:toasts.message_not_found'))
        setEditingMessage(null)
        setIsChatLoading(false)
        return
      }

      // Archive the assistant response AND user question before truncating
      const assistantAfter = chatMessages[messageIndex + 1]
      const userMessageBeforeEdit = chatMessages[messageIndex]
      const hasArchiveContent = assistantAfter?.content?.trim().length
      if (assistantAfter && assistantAfter.role === 'assistant' && hasArchiveContent) {
        setResponseVersions((prev) => {
          const next = new Map(prev)
          const existing = next.get(editingMessageId)
          const entry: V1VersionEntry = existing
            ? { ...existing, responses: [...existing.responses], userMessages: [...(existing.userMessages || [])] }
            : { responses: [], userMessages: [], currentIndex: 0, liveMessage: null, liveUserMessage: null }

          if (entry.liveMessage) {
            if (!entry.responses.some((r) => r.id === entry.liveMessage!.id)) {
              entry.responses.push(entry.liveMessage)
              entry.userMessages.push(entry.liveUserMessage ?? userMessageBeforeEdit)
            }
            entry.liveMessage = null
            entry.liveUserMessage = null
          }

          if (!entry.responses.some((r) => r.id === assistantAfter.id)) {
            entry.responses.push(assistantAfter)
            entry.userMessages.push(userMessageBeforeEdit)
          }

          entry.currentIndex = entry.responses.length
          next.set(editingMessageId, entry)
          return next
        })
      }

      // Clean up version entries for user messages that will be deleted
      const deletedUserMsgIds = chatMessages
        .slice(messageIndex + 1)
        .filter((m) => m.role === 'user')
        .map((m) => m.id)
      if (deletedUserMsgIds.length > 0) {
        setResponseVersions((prev) => {
          const next = new Map(prev)
          for (const id of deletedUserMsgIds) {
            next.delete(id)
          }
          return next
        })
      }

      // Truncate local state to include only messages up to and including the edited one
      const truncatedMessages = chatMessages.slice(0, messageIndex + 1)
      // Update the edited message's content and timestamp in the truncated array
      truncatedMessages[messageIndex] = {
        ...truncatedMessages[messageIndex],
        content: newContent,
        timestamp: new Date().toISOString(),
      }
      setChatMessages(truncatedMessages)

      // Only close edit mode if we're still editing the same message (race condition prevention)
      setEditingMessage((current) => current?.id === editingMessageId ? null : current)

      // Clear input
      setLocalInputValue('')

      // Capture old message ID before updating ref to ensure old handler gets unregistered
      const oldMessageId = currentMessageIdRef.current

      // Auto-trigger AI response with regenerate mode (skip saving user message since it already exists)
      const messageId = `edit-${Date.now()}`

      // Unregister old stop handler to prevent memory leak
      if (oldMessageId) {
        unregisterStopHandler(oldMessageId)
      }

      currentMessageIdRef.current = messageId
      startStreamingResponse({
        conversationId: currentConversationId,
        messageId,
        input: newContent,
        regenerate: true,
      })
    } catch (error) {
      toast.error(t('chat:toasts.edit_error', { error: String(error) }))
      setIsChatLoading(false)
    }
  }, [editingMessage, currentConversationId, chatMessages, setChatMessages, setIsChatLoading, startStreamingResponse])

  const currentConversation = useMemo(
    () => chatConversations.find((c) => c.id === currentConversationId),
    [chatConversations, currentConversationId]
  )

  // Calculate messages after the editing message (memoized to avoid O(n²) on every render)
  const messagesAfterEditingCount = useMemo(() => {
    if (!editingMessage) return 0
    const editingIndex = chatMessages.findIndex(msg => msg.id === editingMessage.id)
    if (editingIndex === -1) return 0
    return chatMessages.length - editingIndex - 1
  }, [editingMessage, chatMessages])

  // Auto-send message when navigating from landing page
  const autoSendTriggeredRef = useRef(false)
  const autoSendTimerRef = useRef<NodeJS.Timeout | null>(null)
  const lastContextIdRef = useRef<string | null>(null)
  const lastNavigationKeyRef = useRef<string | null>(null)
  const contextInitializedRef = useRef(false)
  const navigationInitializedRef = useRef(false)

  // Reset the trigger flag when context_id changes (new board/workspace)
  // OR when navigationKey changes (new navigation from main screen)
  // BUT skip reset on initial context_id/navigationKey assignment to preserve auto-send message
  useEffect(() => {
    const contextChanged = context_id !== lastContextIdRef.current
    // Normalize empty strings to null for consistent comparison
    const normalizedNavKey = navigationKey && navigationKey.trim() !== '' ? navigationKey : null
    const navigationChanged = normalizedNavKey !== lastNavigationKeyRef.current && normalizedNavKey !== null

    // Only reset if:
    // 1. Navigation changed AND navigation was already initialized (subsequent navigation, not first), OR
    // 2. Context changed AND context was already initialized (board switch, not initial load)
    const shouldReset =
      (navigationChanged && navigationInitializedRef.current) ||
      (contextChanged && contextInitializedRef.current)

    if (shouldReset) {
      autoSendTriggeredRef.current = false
      setPendingAutoSendMessage(null)
    }

    // Update refs
    lastContextIdRef.current = context_id || null
    lastNavigationKeyRef.current = normalizedNavKey

    // Mark context as initialized once we have a non-null context_id
    if (context_id) {
      contextInitializedRef.current = true
    }

    // Mark navigation as initialized once we have a non-null navigationKey
    if (normalizedNavKey) {
      navigationInitializedRef.current = true
    }
  }, [context_id, navigationKey])

  useEffect(() => {
    // pendingAutoSendMessage being non-null implies autoSendMessage was true when captured,
    // so we don't need to re-check the prop (which may have been cleared from location.state).
    const shouldTriggerAutoSend =
      !autoSendTriggeredRef.current &&
      pendingAutoSendMessage &&
      pendingAutoSendMessage.trim() &&
      context_id &&
      !isChatLoading

    // Only auto-send once when all conditions are met
    if (shouldTriggerAutoSend) {
      // Clear any existing timer
      if (autoSendTimerRef.current) {
        clearTimeout(autoSendTimerRef.current)
      }

      // Store timer in ref so it persists across renders
      // Small delay to ensure DOM and state are settled
      autoSendTimerRef.current = setTimeout(() => {
        // Force new conversation by clearing currentConversationId right before sending
        // This ensures we always create a new conversation when auto-sending from main screen
        setCurrentConversationId(null)
        // Set flag inside timeout to ensure it only gets set if message actually sends
        autoSendTriggeredRef.current = true
        handleSendMessage(pendingAutoSendMessage)
        setPendingAutoSendMessage(null)
        autoSendTimerRef.current = null
      }, 300)
    }

    // Cleanup function to prevent memory leaks
    return () => {
      if (autoSendTimerRef.current) {
        clearTimeout(autoSendTimerRef.current)
        autoSendTimerRef.current = null
      }
    }
  }, [
    pendingAutoSendMessage,
    context_id,
    isChatLoading,
    handleSendMessage,
  ])

  const handleBackToRecent = () => {
    abortStreamRequest()
    setCurrentConversationId(null)
    setChatMessages([])
    setResponseVersions(new Map())
    // Reset to plant_support_engineer or use selectedAgent if available
    const chatAgent = mapAgentIdToChatAgent(selectedAgent?.id)
    setActiveAgent(chatAgent || 'plant_support_engineer')

    // Mark explicit "new chat" preference so it survives navigation
    try {
      if (context_id) {
        localStorage.setItem(`lastConversation_${context_id}`, 'new')
      }
    } catch {
      // Ignore localStorage errors (e.g., Safari private browsing)
    }
  }

  // Calculate filtered agents for keyboard navigation — must match the autocomplete component's list
  // FS-574: unavailable agents are listed in the store but not mentionable.
  const getFilteredAgents = () => {
    const storeIds = storeAgents && storeAgents.length > 0
      ? storeAgents.filter((a) => a.isAvailable).map((a) => a.id)
      : undefined
    const baseAgents = storeIds
      ? storeIds.map((id) => AGENT_REGISTRY[id]).filter(Boolean) as AgentOption[]
      : AGENTS

    if (!mentionFilterQuery) return baseAgents

    return baseAgents.filter((agent) => {
      const query = mentionFilterQuery.toLowerCase()
      return (
        agent.id.toLowerCase().includes(query) ||
        agent.label.toLowerCase().includes(query) ||
        agent.description.toLowerCase().includes(query)
      )
    })
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Handle autocomplete navigation
    if (showMentionAutocomplete) {
      const filteredAgents = getFilteredAgents()

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setMentionSelectedIndex((prev) =>
          prev < filteredAgents.length - 1 ? prev + 1 : 0
        )
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setMentionSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredAgents.length - 1
        )
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        // Check both that array has items AND that selectedIndex is within bounds
        if (
          filteredAgents.length > 0 &&
          mentionSelectedIndex < filteredAgents.length
        ) {
          handleAgentSelect(filteredAgents[mentionSelectedIndex])
        }
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setShowMentionAutocomplete(false)
        return
      }
    }

    // Normal Enter key handling (Shift+Enter for new line)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  // Handle agent selection from autocomplete
  const handleAgentSelect = (agent: AgentOption) => {
    const textBeforeMention = inputValue.slice(0, cursorPosition)
    const textAfterMention = inputValue.slice(cursorPosition)
    const lastAtIndex = textBeforeMention.lastIndexOf('@')

    if (lastAtIndex !== -1) {
      const newValue =
        textBeforeMention.slice(0, lastAtIndex) +
        agent.name +
        ' ' +
        textAfterMention
      setLocalInputValue(newValue)

      // Set cursor position after the mention
      setTimeout(() => {
        const newCursorPos = lastAtIndex + agent.name.length + 1
        inputRef.current?.setSelectionRange(newCursorPos, newCursorPos)
        inputRef.current?.focus()
      }, 0)
    }

    setShowMentionAutocomplete(false)
    setMentionSelectedIndex(0)
  }

  // Detect @ symbol for autocomplete
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    const position = e.target.selectionStart ?? 0

    setLocalInputValue(value)
    setCursorPosition(position)

    // Check if @ was just typed
    const textBeforeCursor = value.slice(0, position)
    const lastAtIndex = textBeforeCursor.lastIndexOf('@')

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1)
      // Show autocomplete if @ is at word boundary and no space after it
      // Increased length limit to 30 to handle longer potential agent names
      if (!textAfterAt.includes(' ') && textAfterAt.length <= 30) {
        setShowMentionAutocomplete(true)
        setMentionFilterQuery(textAfterAt) // Set filter query for fuzzy search
        setMentionSelectedIndex(0)
      } else {
        setShowMentionAutocomplete(false)
        setMentionFilterQuery('')
      }
    } else {
      setShowMentionAutocomplete(false)
      setMentionFilterQuery('')
    }
  }

  const handleTranscript = async (transcript: string) => {
    setLocalInputValue(transcript)
    setAISpeaking(false)
    // Wait for stop to complete before toggling audio to avoid race condition
    // where handleAudioToggle aborts the active stream before handleStopResponse's
    // finally block can check it, causing isChatLoading to get stuck
    await handleStopResponse()
    handleAudioToggle()
  }

  const handleAudioToggle = () => {
    abortStreamRequest()

    const needsReset = audioOutEnabled || isAISpeaking

    if (needsReset) {
      setAudioOutEnabled(false)
      setAISpeaking(false)
      flushSpeech()
    }

    setAudioOutEnabled(true)

    if (isChatLoading) {
      const lastAssistantMessage = chatMessages
        .filter((msg) => msg.role === ROLES.assistant)
        .pop()

      if (lastAssistantMessage?.content.trim()) {
        addToSpeechQueue(lastAssistantMessage.content)
      }
    }
  }

  useEffect(() => {
    setGlobalStopResponse(handleStopResponse)
    return () => setGlobalStopResponse(() => {})
  }, [handleStopResponse, setGlobalStopResponse])

  const CANONICAL_TO_STORE_AGENT_ID: Record<string, string> = {
    [AgentId.PLANT_SUPPORT]: 'plant-support',
    [AgentId.CHEMICAL]: 'chemical-agent',
    [AgentId.MECHANICAL]: 'mechanical-agent',
    [AgentId.ELECTRICAL]: 'electrical-agent',
  }

  // Get agent display info for visual indicator (uses enriched titles from store when available)
  const getAgentInfo = (agent: string | undefined) => {
    // Color mapping uses canonical agent IDs only - normalizeAgentName handles legacy names
    const agentMap: Record<
      string,
      { name: string; color: string; bgColor: string }
    > = {
      [AgentId.PLANT_SUPPORT]: {
        name: 'AI Plant Support Engineer',
        color: '#D97706',
        bgColor: '#FEF08A',
      },
      [AgentId.CHEMICAL]: {
        name: 'AI Chemical Engineer',
        color: '#2563EB',
        bgColor: '#BFDBFE',
      },
      [AgentId.MECHANICAL]: {
        name: 'AI Mechanical Engineer',
        color: '#16A34A',
        bgColor: '#BBF7D0',
      },
      [AgentId.ELECTRICAL]: {
        name: 'AI Electrical Engineer',
        color: '#9333EA',
        bgColor: '#E9D5FF',
      },
    }
    const normalizedAgent = normalizeAgentName(agent || AgentId.PLANT_SUPPORT)
    const base =
      agentMap[normalizedAgent] ?? agentMap[AgentId.PLANT_SUPPORT]
    const storeAgentId = CANONICAL_TO_STORE_AGENT_ID[normalizedAgent]
    const customTitle = storeAgentId
      ? storeAgents.find((a) => a.id === storeAgentId)?.title
      : undefined
    return {
      ...base,
      name: customTitle ?? base.name,
    }
  }

  const agentInfo = getAgentInfo(activeAgent)

  return (
    <div className="w-full flex flex-col h-full bg-white dark:bg-dark-card rounded-lg relative">
      <ChatHeader
        title={agentInfo.name}
        agentColor={agentInfo.color}
        onClose={isCanvasChat ? onClose : undefined}
      >
        {chatMode === 'global' && (
          <div className="flex gap-3">
            <Select value={documentSelectVal} onValueChange={onGlobalDocSelect}>
              <SelectTrigger className="w-full">
                <SelectValue
                  defaultValue={'generic'}
                  placeholder={t('chat:interface.select_board')}
                />
              </SelectTrigger>
              <SelectContent>
                {boardList.map((board) => {
                  return (
                    <SelectItem key={board.id} value={board.id}>
                      <span
                        className="block truncate max-w-[18rem]"
                        title={board.name}
                      >
                        {board.name}
                      </span>
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
          </div>
        )}
        {callsEnabled && (
          <ZoomMeeting>
            <LLMCallDropdown />
          </ZoomMeeting>
        )}
        <ChatVoice
          onTranscript={handleTranscript}
          handleSendMessage={handleSendMessage}
          isRecording={isRecording}
          setIsRecording={setIsRecording}
          useAzure={!!AZURE_CONFIG.subscriptionKey}
          azureConfig={AZURE_CONFIG}
          onAudioStop={handleStopResponse}
        />
      </ChatHeader>
      {currentConversationId && (
        <ContextHeader
          message={currentConversation?.title || currentConversation?.first_message || chatMessages.find((m) => m.role === 'user')?.content || ''}
          onBack={handleBackToRecent}
        />
      )}
      <div
        className={clsx(
          'flex flex-col flex-1 min-h-0 relative',
          !isCanvasChat && 'w-full items-center bg-gray-50 dark:bg-dark-muted',
          isRTL && 'text-right'
        )}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div
          className={clsx(
            'flex flex-col flex-1 min-h-0',
            isCanvasChat ? 'w-full' : 'w-full px-4 md:px-0 md:w-10/12 lg:w-8/12 max-w-5xl'
          )}
        >
          {chatMessages.length === 0 && !currentConversationId && !messagesLoading && (
            // FS-554: EmptyChatState is `h-full` and was rendered with no scroll
            // container, so a short/resized panel could spill the agent grid over
            // the composer — the same root cause as the empty-conversation state
            // below. Give it a `flex-1 min-h-0 overflow-y-auto` box so overflow
            // stays within the messages area.
            <div className="flex-1 min-h-0 overflow-y-auto">
              <EmptyChatState activeAgent={activeAgent} />
            </div>
          )}
          {chatMessages.length === 0 && currentConversationId && !messagesLoading && (
            // FS-554: EmptyChatState is `h-full`, so rendering it directly above
            // the "no messages yet" hint made it consume 100% of this box and
            // push the hint past the bottom — overflowing into the message
            // composer (worst in the narrow V2 board panel). Put the welcome
            // screen in its own `flex-1 min-h-0 overflow-y-auto` box so it shares
            // vertical space with the hint (kept as a separate, always-visible
            // sibling) and any residual overflow scrolls within the messages area
            // instead of spilling over the footer.
            <div className="flex-1 min-h-0 flex flex-col items-center p-4">
              <div className="flex-1 min-h-0 w-full overflow-y-auto">
                <EmptyChatState activeAgent={activeAgent} />
              </div>
              <p className="text-gray-500 dark:text-dark-text-muted mt-4 text-center shrink-0">
                {t('chat:interface.no_messages_yet')}
              </p>
            </div>
          )}
          {/* Placeholder while messages are being fetched for the selected conversation —
              keeps the panel from flashing the empty-state agent logo before the chat history loads. */}
          {chatMessages.length === 0 && messagesLoading && (
            <div className="flex-1" />
          )}
          {chatMessages.length > 0 && (
            <Messages
              conversationId={currentConversationId}
              conversationLoading={isChatLoading}
              messages={chatMessages}
              handleFeedbackSubmitted={handleFeedbackSubmitted}
              onEditClick={handleEditClick}
              isEditDisabled={isChatLoading}
              editingMessageId={editingMessage?.id || null}
              isEditSaving={isChatLoading && editingMessage !== null}
              messagesAfterCount={messagesAfterEditingCount}
              onEditConfirm={handleEditConfirm}
              onEditCancel={handleEditCancel}
              onRegenerate={handleRegenerate}
              isRegenerateDisabled={isChatLoading}
              getVersionInfo={getVersionInfo}
              getUserVersionInfo={getUserVersionInfo}
              onNavigateVersion={navigateVersion}
              toolProgress={toolProgress}
            />
          )}
        </div>
      </div>
      <footer
        className={clsx(
          'flex flex-col gap-2 px-3 py-2 border-t border-gray-300 dark:border-gray-600',
          isCanvasChat ? 'w-full' : 'w-full md:px-0 md:w-10/12 lg:w-8/12 max-w-5xl self-center'
        )}
      >
        <div
          className={clsx(
            'flex gap-2 items-start',
            isRTL && 'flex-row-reverse'
          )}
        >
          <div className="flex flex-col gap-1 flex-1 relative">
            <AgentMentionAutocomplete
              isVisible={showMentionAutocomplete}
              selectedIndex={mentionSelectedIndex}
              filterQuery={mentionFilterQuery}
              onSelect={handleAgentSelect}
              onClose={() => setShowMentionAutocomplete(false)}
              availableAgentIds={storeAgents && storeAgents.length > 0 ? storeAgents.filter((a) => a.isAvailable).map((a) => a.id) : undefined}
            />
            {/* Chat scope chips — docs the user attached via board card kebab.
                Rendered inside the input-card wrapper (above the textarea) so
                the V2 panel CSS treats them as part of the same card. */}
            {/* FS-537: capped context chips — the row can no longer grow
                unbounded and collapse into the textarea; chips beyond the cap
                fold into a "+N more" pill that opens an all-files modal. */}
            <ChatContextChips chips={chatContextChips} onRemove={removeDocFromChatContext} />
            <Textarea
              ref={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onInput={handleTextareaResize}
              placeholder={
                isAnnotator
                  ? t('chat:interface.annotatorReadOnly', {
                      defaultValue: 'AI chat is unavailable for your access level.',
                    })
                  : animatedPlaceholder
              }
              onKeyDown={handleKeyDown}
              disabled={isAnnotator}
              className={clsx('h-fit', isRTL ? 'text-right' : 'text-left')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
          <div className={clsx('flex items-center self-start', isRTL && 'flex-row-reverse')}>
            {isChatLoading ? (
              <Button
                onClick={handleStopResponse}
                aria-label={t('chat.stop_generation')}
                title={t('chat.stop_generation')}
                className="w-[40px] h-[40px] flex items-center justify-center rounded-full bg-orange-500 hover:bg-orange-600"
              >
                <Square className="h-5 w-5" aria-hidden="true" />
              </Button>
            ) : (
              <Button
                id={CHAT_SEND_BUTTON_ID}
                data-testid={CHAT_SEND_BUTTON_ID}
                onClick={() => handleSendMessage()}
                className="w-[40px] h-[40px] flex items-center justify-center rounded-full bg-orange-500 hover:bg-orange-600"
                disabled={isAnnotator || !inputValue.trim()}
              >
                <Send className="w-5 h-5" />
              </Button>
            )}
          </div>
        </div>
      </footer>
    </div>
  )
}

export default ChatInterface
